import { Box, Stack } from "@vapor/react-material";
import dayjs from "dayjs";
import { type ReactElement, useMemo } from "react";
import type { FulfilmentDto } from "../../../../../core/api/use-get-fulfilments/FulfilmentDto";
import type { FulfilmentsDto } from "../../../../../core/api/use-get-fulfilments/FulfilmentsDto";
import { FulfilmentUnlinked } from "../fulfilment-unlinked/FulfilmentUnlinked";
import { Fulfilment } from "../fulfilment/Fulfilment";
import { groupFulfilmentsByDeadline } from "./groupFulfilmentsByDeadline";

export interface FulfilmentListProps<FulfilmentsDto> {
    fulfilments: FulfilmentsDto;
    onClickFulfilment?: (fulfilment: FulfilmentDto) => unknown;
    onClickEditCustomFulfilment?: (fulfilment: FulfilmentDto) => unknown;
    onClickLinkTaxPayersToFulfilment: (fulfilment: FulfilmentDto) => unknown;
}

export const FulfilmentList = ({
    fulfilments,
    onClickFulfilment,
    onClickEditCustomFulfilment,
    onClickLinkTaxPayersToFulfilment,
}: FulfilmentListProps<FulfilmentsDto>): ReactElement => {
    const groupedFulfilmentsByDeadline = useMemo(
        () => groupFulfilmentsByDeadline(fulfilments),
        [fulfilments],
    );

    return (
        <Stack>
            {groupedFulfilmentsByDeadline.map((group) => (
                <Box
                    key={group.deadline.toISOString()}
                    sx={{ marginBottom: 2 }}
                >
                    {group.fulfilments.map((fulfilment, index) => (
                        <Box key={index}>
                            {!!fulfilment && !!fulfilment.taxPayerCounters ? (
                                <Fulfilment
                                    taxPayerActivityCounters={{
                                        total: fulfilment.taxPayerCounters
                                            .total,
                                        closed: fulfilment.taxPayerCounters
                                            .closed,
                                        completed:
                                            fulfilment.taxPayerCounters
                                                .completed,
                                        completedWithWarnings:
                                            fulfilment.taxPayerCounters
                                                .completedWithWarnings,
                                        pending:
                                            fulfilment.taxPayerCounters.pending,
                                        linked: fulfilment.taxPayerCounters
                                            .linked,
                                    }}
                                    date={dayjs(fulfilment.deadline)}
                                    description={fulfilment.description}
                                    isCustomFulfilment={fulfilment.custom}
                                    isFirstFulfilmentInGroup={index === 0}
                                    key={fulfilment.levyId}
                                    levyId={fulfilment.levyId}
                                    year={dayjs(fulfilment.deadline).year()}
                                    onClick={() => {
                                        if (onClickFulfilment)
                                            onClickFulfilment(
                                                fulfilment as FulfilmentDto,
                                            );
                                    }}
                                    onClickLinkTaxPayers={() => {
                                        onClickLinkTaxPayersToFulfilment(
                                            fulfilment,
                                        );
                                    }}
                                    onClickEditCustomFulfilment={() => {
                                        if (onClickEditCustomFulfilment) {
                                            onClickEditCustomFulfilment(
                                                fulfilment as FulfilmentDto,
                                            );
                                        }
                                    }}
                                    title={fulfilment.title}
                                    withTaxTinderSuggestion={
                                        fulfilment.withTaxTinderSuggestion
                                    }
                                />
                            ) : (
                                <FulfilmentUnlinked
                                    date={dayjs(fulfilment.deadline)}
                                    description={fulfilment.description}
                                    isCustomFulfilment={fulfilment.custom}
                                    key={fulfilment.levyId}
                                    levyId={fulfilment.levyId}
                                    onClickLinkTaxPayers={() => {
                                        onClickLinkTaxPayersToFulfilment(
                                            fulfilment,
                                        );
                                    }}
                                    onClickEditCustomFulfilment={() => {
                                        if (onClickEditCustomFulfilment) {
                                            onClickEditCustomFulfilment(
                                                fulfilment as FulfilmentDto,
                                            );
                                        }
                                    }}
                                    title={fulfilment.title}
                                    header={index === 0}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            ))}
        </Stack>
    );
};
